import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Menu,
  MenuItem,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Pagination
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  MarkunreadMailbox as UnreadIcon,
  MarkEmailRead as ReadIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI, visitorsAPI } from '../services/api.js';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';

const NotificationsDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  const [sortOrder, setSortOrder] = useState('NEWEST');
  const [dateRange, setDateRange] = useState('ALL');
  const [page, setPage] = useState(1);
  const [visitorActionError, setVisitorActionError] = useState('');
  const [visitorActionLoading, setVisitorActionLoading] = useState(false);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [markAllError, setMarkAllError] = useState('');
  const pageSize = 10;

  const userId = user?.userId || user?.attributes?.sub || user?.username;
  const groupNames = (user?.groups || []).map(group => typeof group === 'string' ? group.toLowerCase() : String(group.name || '').toLowerCase());
  const userRole = String(user?.role || '').toLowerCase();
  const canManageVisitorApproval = groupNames.some(group => ['owner', 'tenant'].includes(group))
    || ['owner', 'tenant', 'resident'].includes(userRole);
  const canLeaveAtGate = canManageVisitorApproval;

  // Fetch user notifications - optimized for dashboard view
  const { data: notifications = [], isLoading, isFetching, error, refetch } = useQuery(
    ['userNotifications', userId, dateRange],
    () => userId ? notificationsAPI.getUserNotifications(userId, dateRange === 'ALL' ? undefined : Number(dateRange)) : Promise.resolve({ data: [] }),
    {
      select: (response) => response.data || [],
      enabled: !!userId && !!user,
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 60000, // Refresh on focus only when data is over one minute old
      cacheTime: 300000, // Keep in cache for 5 minutes
      retry: 1, // Only retry once on failure
      retryDelay: 3000, // Wait 3 seconds before retry
    }
  );

  // Mark notification as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => notificationsAPI.markAsRead(notificationId),
    {
      onSuccess: (_response, notificationId) => {
        queryClient.setQueriesData(['userNotifications', userId], current => {
          if (!Array.isArray(current?.data)) return current;
          return {
            ...current,
            data: current.data.map(item => (
              item.notificationId === notificationId ? { ...item, isRead: true } : item
            ))
          };
        });
        setSelectedNotification(current => (
          current?.notificationId === notificationId ? { ...current, isRead: true } : current
        ));
        queryClient.invalidateQueries(['userNotifications', userId]);
      },
      onError: error => console.error('Failed to persist notification read state:', error),
    }
  );

  useEffect(() => {
    const notificationId = location.state?.selectedNotificationId;
    if (!notificationId || !Array.isArray(notifications)) return;
    const notification = notifications.find(item => item.notificationId === notificationId);
    if (notification) {
      setSelectedNotification(notification);
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate, notifications]);

  useEffect(() => {
    setPage(1);
  }, [selectedFilter, sortOrder, dateRange]);

  // Don't render dashboard if user is not logged in
  if (!user || !userId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Please log in to view notifications</Typography>
      </Box>
    );
  }

  // Priority configurations
  const priorityConfig = {
    LOW: { color: 'success', icon: <InfoIcon />, bgColor: '#e8f5e8' },
    MEDIUM: { color: 'warning', icon: <InfoIcon />, bgColor: '#fff8e1' },
    HIGH: { color: 'error', icon: <WarningIcon />, bgColor: '#ffebee' },
    URGENT: { color: 'error', icon: <ErrorIcon />, bgColor: '#ffcdd2' }
  };

  // Category configurations
  const categoryConfig = {
    MAINTENANCE: { label: 'Maintenance', emoji: '🔧' },
    UTILITY: { label: 'Utilities', emoji: '⚡' },
    FINANCIAL: { label: 'Financial', emoji: '💰' },
    SECURITY: { label: 'Security', emoji: '🔒' },
    COMMUNITY: { label: 'Community', emoji: '👥' },
    POLICY: { label: 'Policy', emoji: '📋' },
    FACILITY: { label: 'Facility', emoji: '🏢' },
    EMERGENCY: { label: 'Emergency', emoji: '🚨' },
    GENERAL: { label: 'General', emoji: '📢' }
  };

  // Filter options
  const filterOptions = [
    { value: 'ALL', label: 'All Notifications' },
    { value: 'UNREAD', label: 'Unread Only' },
    { value: 'HIGH_PRIORITY', label: 'High Priority' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'FINANCIAL', label: 'Financial' },
    { value: 'COMMUNITY', label: 'Community' },
    { value: 'EMERGENCY', label: 'Emergency' }
  ];

  // Filter notifications based on selected filter
  const filteredNotifications = (Array.isArray(notifications) ? notifications : []).filter(notification => {
    switch (selectedFilter) {
      case 'UNREAD':
        return !notification.isRead;
      case 'HIGH_PRIORITY':
        return ['HIGH', 'URGENT'].includes(notification.priority);
      case 'MAINTENANCE':
      case 'FINANCIAL':
      case 'COMMUNITY':
      case 'EMERGENCY':
        return notification.category === selectedFilter;
      default:
        return true;
    }
  });
  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    const first = new Date(a.createdAt || 0).getTime();
    const second = new Date(b.createdAt || 0).getTime();
    return sortOrder === 'NEWEST' ? second - first : first - second;
  });
  const totalPages = Math.max(1, Math.ceil(sortedNotifications.length / pageSize));
  const effectivePage = Math.min(page, totalPages);
  const paginatedNotifications = sortedNotifications.slice((effectivePage - 1) * pageSize, effectivePage * pageSize);

  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleMarkAllAsRead = async () => {
    if (markAllLoading) return;
    setMarkAllLoading(true);
    setMarkAllError('');
    try {
      const response = await notificationsAPI.getUserNotifications(userId);
      const unreadIds = (Array.isArray(response.data) ? response.data : [])
        .filter(item => !item.isRead)
        .map(item => item.notificationId);
      const results = await Promise.allSettled(unreadIds.map(notificationsAPI.markAsRead));
      const failed = results.filter(result => result.status === 'rejected').length;

      queryClient.setQueriesData(['userNotifications', userId], current => {
        if (!Array.isArray(current?.data)) return current;
        const successfulIds = new Set(unreadIds.filter((_id, index) => results[index].status === 'fulfilled'));
        return {
          ...current,
          data: current.data.map(item => (
            successfulIds.has(item.notificationId) ? { ...item, isRead: true } : item
          ))
        };
      });
      await queryClient.invalidateQueries(['userNotifications', userId]);
      if (failed) setMarkAllError(`Failed to mark ${failed} notification(s) as read. Please try again.`);
    } catch (markAllRequestError) {
      setMarkAllError(markAllRequestError.response?.data?.message || 'Failed to mark notifications as read. Please try again.');
    } finally {
      setMarkAllLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setDialogOpen(true);
    setVisitorActionError('');
    
    // Mark as read if unread
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.notificationId);
    }
  };

  const handleVisitorAction = async (actionStatus) => {
    if (!selectedNotification?.buildingId || !selectedNotification?.relatedEntityId) {
      setVisitorActionError('This notification does not contain the visitor details required to complete the action.');
      return;
    }
    const approvalStatus = actionStatus === 'LEAVE_AT_GATE' ? 'Leave At Gate' : actionStatus;
    setVisitorActionLoading(true);
    setVisitorActionError('');
    try {
      await visitorsAPI.update(
        selectedNotification.buildingId,
        selectedNotification.relatedEntityId,
        { approvalStatus }
      );
      await notificationsAPI.update(selectedNotification.notificationId, { actionStatus });
      setSelectedNotification(current => ({ ...current, actionStatus, isRead: true }));
      queryClient.invalidateQueries(['userNotifications', userId]);
    } catch (actionError) {
      setVisitorActionError(actionError.response?.data?.message || 'Failed to update the visitor request.');
    } finally {
      setVisitorActionLoading(false);
    }
  };

  // Format notification date
  const formatNotificationDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM dd, yyyy HH:mm');
    }
  };

  // Get notification avatar color based on category and read status
  const getAvatarProps = (notification) => {
    const config = categoryConfig[notification.category] || categoryConfig.GENERAL;
    const priority = priorityConfig[notification.priority] || priorityConfig.MEDIUM;
    
    return {
      sx: {
        bgcolor: notification.isRead ? 'grey.300' : priority.bgColor,
        color: notification.isRead ? 'grey.600' : priority.color + '.main'
      },
      children: config.emoji
    };
  };

  if (!userId) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="error">
            Please log in to view notifications
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load notifications: {error.message}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.5, md: 2 },
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, minWidth: 0 }}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          </Badge>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              lineHeight: 1.2,
              minWidth: 0,
              wordBreak: 'normal'
            }}
          >
            {t('notifications.title')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' }, width: { xs: '100%', md: 'auto' } }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            variant="outlined"
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            startIcon={<ReadIcon />}
            onClick={handleMarkAllAsRead}
            variant="contained"
            disabled={unreadCount === 0 || markAllLoading}
          >
            {markAllLoading ? 'Marking...' : 'Mark all as read'}
          </Button>
          <Button
            startIcon={<FilterIcon />}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            variant="outlined"
            sx={{
              maxWidth: { xs: '100%', md: 280 },
              '& .MuiButton-endIcon, & .MuiButton-startIcon': { flexShrink: 0 }
            }}
          >
            {filterOptions.find(f => f.value === selectedFilter)?.label}
          </Button>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} label="Order" onChange={event => setSortOrder(event.target.value)}>
              <MenuItem value="NEWEST">Newest first</MenuItem>
              <MenuItem value="OLDEST">Oldest first</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 145 } }}>
            <InputLabel>Date range</InputLabel>
            <Select value={dateRange} label="Date range" onChange={event => setDateRange(event.target.value)}>
              <MenuItem value="ALL">All time</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        TransitionComponent={Fade}
      >
        {filterOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={selectedFilter === option.value}
            onClick={() => {
              setSelectedFilter(option.value);
              setFilterAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {markAllError && <Alert severity="error" sx={{ mb: 2 }}>{markAllError}</Alert>}

      {/* Summary Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: { xs: 1.25, sm: 2 },
          mb: 3
        }}
      >
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Badge badgeContent={notifications.length} color="primary" showZero>
              <NotificationsIcon />
            </Badge>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">{notifications.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total</Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Badge badgeContent={unreadCount} color="error" showZero>
              <UnreadIcon />
            </Badge>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">{unreadCount}</Typography>
              <Typography variant="body2" color="text.secondary">Unread</Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Badge badgeContent={notifications.filter(n => ['HIGH', 'URGENT'].includes(n.priority)).length} color="warning" showZero>
              <WarningIcon />
            </Badge>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">
                {notifications.filter(n => ['HIGH', 'URGENT'].includes(n.priority)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">High Priority</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Notifications List */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading notifications...</Typography>
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsNoneIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {selectedFilter === 'UNREAD' ? 'No unread notifications' : 'No notifications found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedFilter === 'ALL' 
                  ? 'You\'re all caught up!' 
                  : 'Try changing the filter to see more notifications.'
                }
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {paginatedNotifications.map((notification, index) => (
                <React.Fragment key={notification.notificationId}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                      borderRadius: 1,
                      mb: 1,
                      px: { xs: 1.25, sm: 2 },
                      py: { xs: 1.5, sm: 2 },
                      alignItems: 'flex-start',
                      gap: { xs: 1, sm: 0 }
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: { xs: 44, sm: 56 }, mt: 0.25 }}>
                      <Avatar
                        {...getAvatarProps(notification)}
                        sx={{
                          ...getAvatarProps(notification).sx,
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}
                      />
                    </ListItemAvatar>
                    
                    <ListItemText
                      sx={{ minWidth: 0, m: 0 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 0.75,
                            minWidth: 0,
                            flexDirection: { xs: 'column', sm: 'row' }
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: notification.isRead ? 'normal' : 'bold',
                              flex: 1,
                              minWidth: 0,
                              width: '100%',
                              fontSize: { xs: '0.98rem', sm: '1rem' },
                              lineHeight: 1.35,
                              display: '-webkit-box',
                              WebkitLineClamp: { xs: 2, sm: 1 },
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flexShrink: 0 }}>
                            <Chip
                              size="small"
                              label={notification.priority}
                              color={priorityConfig[notification.priority]?.color || 'default'}
                            />
                            {!notification.isRead && (
                              <Chip size="small" label="NEW" color="primary" />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              color: notification.isRead ? 'text.secondary' : 'text.primary',
                              mb: 0.75,
                              fontSize: { xs: '0.9rem', sm: '0.875rem' },
                              lineHeight: 1.45
                            }}
                          >
                            {notification.message}
                          </Typography>
                          {notification.attachmentUrl && (
                            <Box
                              component="img"
                              src={notification.attachmentUrl}
                              alt={notification.attachment?.fileName || 'Notification attachment'}
                              sx={{ display: 'block', width: 120, height: 72, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                            />
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={categoryConfig[notification.category]?.label || notification.category}
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {formatNotificationDate(notification.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />

                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, ml: 1 }}>
                      {notification.isRead ? (
                        <Tooltip title="Read">
                          <ReadIcon color="action" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Unread">
                          <UnreadIcon color="primary" />
                        </Tooltip>
                      )}
                    </Box>
                  </ListItem>
                  
                  {index < paginatedNotifications.length - 1 && <Divider variant="inset" />}
                </React.Fragment>
              ))}
            </List>
          )}
          {!isLoading && sortedNotifications.length > 0 && (
            <Box
              component="nav"
              aria-label="Notification pagination"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mt: 2,
                mb: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))', sm: 0 },
                px: 1,
                py: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Page {effectivePage} of {totalPages} · {sortedNotifications.length} notification{sortedNotifications.length === 1 ? '' : 's'}
              </Typography>
              <Pagination
                count={totalPages}
                page={effectivePage}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size="small"
                siblingCount={0}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Notification Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        {selectedNotification && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar {...getAvatarProps(selectedNotification)} />
                  <Box>
                    <Typography variant="h6">{selectedNotification.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={selectedNotification.priority}
                        color={priorityConfig[selectedNotification.priority]?.color || 'default'}
                      />
                      <Chip
                        size="small"
                        label={categoryConfig[selectedNotification.category]?.label || selectedNotification.category}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={() => setDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              {selectedNotification.metadata?.visitorPhotoUrl && (
                <Box
                  component="img"
                  src={selectedNotification.metadata.visitorPhotoUrl}
                  alt={selectedNotification.metadata.visitorName || 'Visitor'}
                  sx={{ display: 'block', width: 140, height: 140, objectFit: 'cover', borderRadius: 2, mb: 2 }}
                />
              )}
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {selectedNotification.message}
              </Typography>

              {selectedNotification.attachmentUrl && (
                <Box
                  component="img"
                  src={selectedNotification.attachmentUrl}
                  alt={selectedNotification.attachment?.fileName || 'Notification attachment'}
                  sx={{ display: 'block', width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 1, mb: 3 }}
                />
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Sent on: {format(new Date(selectedNotification.createdAt), 'MMMM dd, yyyy HH:mm')}
                </Typography>
              </Box>

              {selectedNotification.isBuildingWide && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This is a building-wide notification sent to multiple residents.
                </Alert>
              )}

              {selectedNotification.type === 'VISITOR_CREATED' && canManageVisitorApproval && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Visitor approval
                  </Typography>
                  {visitorActionError && <Alert severity="error" sx={{ mb: 1.5 }}>{visitorActionError}</Alert>}
                  {selectedNotification.actionStatus ? (
                    <Alert severity={selectedNotification.actionStatus === 'REJECTED' ? 'error' : 'success'}>
                      Action completed: {selectedNotification.actionStatus.replaceAll('_', ' ')}
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={visitorActionLoading}
                        onClick={() => handleVisitorAction('APPROVED')}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={visitorActionLoading}
                        onClick={() => handleVisitorAction('REJECTED')}
                      >
                        Reject
                      </Button>
                      {canLeaveAtGate && selectedNotification.metadata?.purpose === 'Delivery Personnel' && (
                        <Button
                          variant="outlined"
                          color="warning"
                          disabled={visitorActionLoading}
                          onClick={() => handleVisitorAction('LEAVE_AT_GATE')}
                        >
                          Leave at Gate
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default NotificationsDashboard;
